import { useState } from "react";
import { Paper, Grid, Typography, IconButton, Collapse, TextField, MenuItem, Button, FormControlLabel, Checkbox } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs'
import validator from 'validator'


const relationshipOptions = [
    "Father", "Mother", "Spouse", "Daughter", "Son", "Brother", "Sister",
    "Grand-Son", "Grand-Daughter", "Grand-Father", "Grand-Mother", "Not Provided"
];

const proofTypeOptions = ["PAN", "Aadhaar", "Voter Id", "Passport"];
const stateOptions = [
  "Andhra Pradesh",
  "Arunachal Pradesh",
  "Assam",
  "Bihar",
  "Chhattisgarh",
  "Goa",
  "Gujarat",
  "Haryana",
  "Himachal Pradesh",
  "Jharkhand",
  "Karnataka",
  "Kerala",
  "Madhya Pradesh",
  "Maharashtra",
  "Manipur",
  "Meghalaya",
  "Mizoram",
  "Nagaland",
  "Odisha",
  "Punjab",
  "Rajasthan",
  "Sikkim",
  "Tamil Nadu",
  "Telangana",
  "Tripura",
  "Uttar Pradesh",
  "Uttarakhand",
  "West Bengal"
];

const NomineeForm = ({ index, open, onToggle, onSave, saved }) => {
    const [nominee, setNominee] = useState({
        nomineeName: '', nomineeRelationship: '', nomineeDob: null, nomineeMobile: '',
        nomineeProofType: '', nomineeProofNumber: '', nomineeEmail: '', nomineeAddress: '',
        nomineePincode: '', nomineeCity: '', nomineeState: '', nomineeCountry: ''
    })

    const [guardian, setGuardian] = useState({
        guardianName: '', guardianRelationship: '', guardianDob: null, guardianMobile: '',
        guardianProofType: '', guardianProofNumber: '', guardianEmail: '', guardianAddress: '',
        guardianPincode: '', guardianCity: '', guardianState: '', guardianCountry: ''
    })

    const [isUnderage, setIsUnderage] = useState(false);
    const [isGuardianage, setIsGuardianage] = useState(false)
    const [isSameAsNominee, setIsSameAsNominee] = useState(false);

    const [errors, setErrors] = useState({})

    const checkAge = (dob) => {
        if (!dob) return 0;

        const birthDate = dayjs(dob, 'YYYY-MM-DD');
        const today = dayjs().startOf('day');

        return today.diff(birthDate, 'year');
    }

    const handleNomineeChange = (field, value) => {
        setNominee(prev => ({ ...prev, [field]: value }));
        setErrors(prev => ({ ...prev, [field]: '' }));

        if (isSameAsNominee) {
            const addressFields = [
                'nomineeAddress', 'nomineePincode',
                'nomineeCity', 'nomineeState', 'nomineeCountry'
            ];

            const otherFieldsToSync = {
                nomineeName: 'guardianName',
                nomineeMobile: 'guardianMobile',
                nomineeEmail: 'guardianEmail',
                nomineeRelationship: 'guardianRelationship',
                nomineeDob: 'guardianDob',
                nomineeProofType: 'guardianProofType',
                nomineeProofNumber: 'guardianProofNumber',
            };

            if (addressFields.includes(field)) {
                const guardianField = field.replace('nominee', 'guardian');
                setGuardian(prev => ({ ...prev, [guardianField]: value }));
            }

            if (Object.keys(otherFieldsToSync).includes(field)) {
                const guardianField = otherFieldsToSync[field];
                setGuardian(prev => ({ ...prev, [guardianField]: value }));
            }
        }
    };

    const handleGuardianChange = (field, value) => {
        setGuardian(prev => ({ ...prev, [field]: value }));
        setErrors(prev => ({ ...prev, [field]: '' }));
    };


    const handleGuardianAddressCheckbox = (checked) => {
        setIsSameAsNominee(checked);

        if (checked) {
            setGuardian(prev => ({
                ...prev,
                guardianAddress: nominee.nomineeAddress,
                guardianPincode: nominee.nomineePincode,
                guardianCity: nominee.nomineeCity,
                guardianState: nominee.nomineeState,
                guardianCountry: nominee.nomineeCountry
            }));
        } else {
            setGuardian(prev => ({
                ...prev,
                guardianAddress: '',
                guardianPincode: '',
                guardianCity: '',
                guardianState: '',
                guardianCountry: ''
            }));
        }
    };


    const validateFieldByField = () => {

        const trimmedName = nominee.nomineeName.trim()
        if (validator.isEmpty(trimmedName)) {
            setErrors({ nomineeName: 'Name is required' });
            return false;
        } else if (!trimmedName.split(' ').every(part => validator.isAlpha(part))) {
            setErrors({ nomineeName: 'Name must contain only letters' });
            return false;
        }

        if (validator.isEmpty(nominee.nomineeRelationship)) {
            setErrors({ nomineeRelationship: 'Relationship is required' });
            return false;
        }

        if (!nominee.nomineeDob) {
            setErrors({ nomineeDob: 'DoB is required' });
            return false;
        }

        if (isUnderage) {
            const mobile = guardian.guardianMobile || ''
            if (!validator.isMobilePhone(mobile, 'en-IN') || !validator.isLength(mobile, { min: 10, max: 10 })) {
                setErrors({ guardianMobile: 'Please enter a valid Mobile Number' });
                return false;
            }

            if (!validator.isEmail(guardian.guardianEmail || '')) {
                setErrors({ guardianEmail: 'Please enter a valid email address' });
                return false;
            }
        } else {
            const mobile = nominee.nomineeMobile || ''
            if (!validator.isMobilePhone(mobile, 'en-IN') || !validator.isLength(mobile, { min: 10, max: 10 })) {
                setErrors({ nomineeMobile: 'Please enter a valid Mobile Number' });
                return false;
            }

            if (!validator.isEmail(nominee.nomineeEmail || '')) {
                setErrors({ nomineeEmail: 'Please enter a valid email address' });
                return false;
            }
        }

        if (validator.isEmpty(nominee.nomineeProofType)) {
            setErrors({ nomineeProofType: 'Proof type is required' });
            return false;
        }

        const id = nominee.nomineeProofNumber.trim();
        const type = nominee.nomineeProofType;

        const isAadhaar = validator.isLength(id, { min: 12, max: 12 }) && validator.isNumeric(id);
        const isPAN = validator.matches(id, /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/);
        const isVoterId = validator.matches(id, /^[A-Z]{3}[0-9]{7}$/);
        const isPassport = validator.matches(id, /^[A-PR-WYa-pr-wy][1-9]\d{6}$/);

        if (validator.isEmpty(id)) {
            setErrors({ nomineeProofNumber: 'ID number is required' });
            return false;
        }

        // Check if format matches selected proof type
        if (
            (type === 'Aadhaar' && !isAadhaar) ||
            (type === 'PAN' && !isPAN) ||
            (type === 'Voter Id' && !isVoterId) ||
            (type === 'Passport' && !isPassport)
        ) {
            setErrors({ nomineeProofNumber: `Invalid ${type} format` });
            return false;
        }


        if (validator.isEmpty(nominee.nomineeAddress.trim())) {
            setErrors({ nomineeAddress: 'Address is required' });
            return false;
        }

        if (!validator.isPostalCode(nominee.nomineePincode || '', 'IN')) {
            setErrors({ nomineePincode: 'Pincode is required' });
            return false;
        }

        if (validator.isEmpty(nominee.nomineeCity.trim())) {
            setErrors({ nomineeCity: 'City is required' });
            return false;
        }

        if (validator.isEmpty(nominee.nomineeState)) {
            setErrors({ nomineeState: 'State is required' });
            return false;
        }

        if (validator.isEmpty(nominee.nomineeCountry.trim())) {
            setErrors({ nomineeCountry: 'Country is required' });
            return false;
        }

        if (isUnderage) {

            const trimmedName = guardian.guardianName.trim()
            if (validator.isEmpty(trimmedName)) {
                setErrors({ guardianName: 'Name is required' });
                return false;
            } else if (!trimmedName.split(' ').every(part => validator.isAlpha(part))) {
                setErrors({ guardianName: 'Name must contain only letters' });
                return false;
            }

            if (validator.isEmpty(guardian.guardianRelationship)) {
                setErrors({ guardianRelationship: 'Relationship with Guardian is required' });
                return false;
            }

            if (!guardian.guardianDob) {
                setErrors({ guardianDob: 'DoB is required' });
                return false;
            }

            if (validator.isEmpty(guardian.guardianProofType)) {
                setErrors({ guardianProofType: 'Proof type is required' });
                return false;
            }

            const id = guardian.guardianProofNumber.trim();

            const isAadhaar = validator.isLength(id, { min: 12, max: 12 }) && validator.isNumeric(id);
            const isPAN = validator.matches(id, /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/);
            const isVoterId = validator.matches(id, /^[A-Z]{3}[0-9]{7}$/);
            const isPassport = validator.matches(id, /^[A-PR-WYa-pr-wy][1-9]\d{6}$/);

            if (validator.isEmpty(id) || (!isAadhaar && !isPAN && !isVoterId && !isPassport)) {
                setErrors({ guardianProofNumber: 'Valid ID is required' });
                return false;
            }

            if (validator.isEmpty(guardian.guardianAddress)) {
                setErrors({ guardianAddress: 'Address is required' });
                return false;
            }

            if (!validator.isPostalCode(guardian.guardianPincode || '', 'IN')) {
                setErrors({ guardianPincode: 'Pincode is required' });
                return false;
            }

            if (validator.isEmpty(guardian.guardianCity)) {
                setErrors({ guardianCity: 'City is required' });
                return false;
            }

            if (validator.isEmpty(guardian.guardianState)) {
                setErrors({ guardianState: 'State is required' });
                return false;
            }

            if (validator.isEmpty(guardian.guardianCountry)) {
                setErrors({ guardianCountry: 'Country is required' });
                return false;
            }
        }

        setErrors({});
        return true;
    };

    const handleSaveNext = () => {
        if (validateFieldByField()) {
            if (isGuardianage) {
                return
            }
            onSave(index, true, nominee, guardian, isUnderage, isSameAsNominee);
        }
    }

    return (
        <Paper
            variant="outlined"
            sx={{
                p: { xs: 2, sm: 3 },
                mt: 2,
                width: '100%',
                background: "#fff",
                borderRadius: 2,
                border: saved ? '2px solid #6B8E23' : '1px solid #817E7E',
            }}
        >
            <Grid container alignItems="center" justifyContent="space-between">
                <Typography
                    sx={{ fontWeight: 600, fontSize: { xs: "16px", sm: "18px" }, color: "#817E7E" }}
                >
                    {nominee.nomineeName ? nominee.nomineeName : `Add Nominee ${index + 1}`}
                </Typography>
                <IconButton onClick={onToggle} sx={{ color: "#817E7E" }}>
                    {open ? <CloseIcon /> : <AddIcon />}
                </IconButton>
            </Grid>

            <Collapse in={open} timeout="auto" unmountOnExit>
                <Grid container spacing={2} mt={1}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Enter Full Name"
                            variant="outlined"
                            size="small"
                            value={nominee.nomineeName}
                            onChange={(e) => handleNomineeChange('nomineeName', e.target.value)}
                            error={!!errors.nomineeName}
                            helperText={errors.nomineeName}
                            InputProps={{
                                sx: {
                                    color: '#000',
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#999',
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#000',
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#999',
                                    },
                                }
                            }}
                            InputLabelProps={{
                                sx: {
                                    color: '#000',

                                    '&.Mui-focused': {
                                        color: '#333333',
                                    }
                                }
                            }}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            select
                            label="Relationship with Nominee"
                            variant="outlined"
                            size="small"
                            value={nominee.nomineeRelationship}
                            onChange={(e) => handleNomineeChange('nomineeRelationship', e.target.value)}
                            error={!!errors.nomineeRelationship}
                            helperText={errors.nomineeRelationship}
                            InputProps={{
                                sx: {
                                    color: '#000',
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#999',
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#000',
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#999',
                                    },
                                    '& .MuiSelect-icon': {
                                        color: '#000',
                                    },
                                }
                            }}
                            InputLabelProps={{
                                sx: {
                                    color: '#000',
                                    '&.Mui-focused': {
                                        color: '#333333',
                                    }
                                }
                            }}

                            SelectProps={{
                                MenuProps: {
                                    PaperProps: {
                                        sx: {
                                            backgroundColor: '#fff',
                                            boxShadow: 'none',
                                            border: '1px solid #ddd',
                                            borderRadius: 1,
                                        }
                                    }
                                }
                            }}
                        >
                            {relationshipOptions.map((opt, idx) => (
                                <MenuItem key={idx} value={opt}
                                    sx={{
                                        background: '#fff',
                                        color: "#000",
                                        '&:hover': {
                                            backgroundColor: '#f0f0f0',
                                        },
                                        '&.Mui-selected': {
                                            backgroundColor: '#e0e0e0',
                                        }
                                    }}
                                >
                                    {opt}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                                label="Date of Birth"
                                format="DD/MM/YYYY"
                                // value={nominee.nomineeDob}
                                value={nominee.nomineeDob ? dayjs(nominee.nomineeDob) : null}
                                onChange={(newVal) => {
                                    if (newVal) {
                                        const dateString  = dayjs(newVal).format('YYYY-MM-DD')
                                        handleNomineeChange('nomineeDob', dateString)
                                        const age = checkAge(dateString)
                                        setIsUnderage(age < 18)
                                    } else {
                                        handleNomineeChange('nomineeDob', null);
                                    }
                                }}
                                maxDate={dayjs()}
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        size: "small",
                                        variant: "outlined",
                                        error: !!errors.nomineeDob,
                                        helperText: errors.nomineeDob,
                                        InputLabelProps: {
                                            sx: {
                                                color: '#000',
                                                '&.Mui-focused': {
                                                    color: '#333333',
                                                },
                                            },
                                        },
                                        InputProps: {
                                            sx: {
                                                color: '#000',
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#999',
                                                },
                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#000',
                                                },
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#999',
                                                },
                                                '& .MuiSvgIcon-root': {
                                                    color: '#000', // calendar icon color
                                                },
                                            },
                                        },

                                    },
                                    desktopPaper: {
                                        sx: {
                                            backgroundColor: '#fff',
                                            color: '#000',
                                            border: '1px solid #ddd',
                                            boxShadow: 'none',
                                            borderRadius: 1,
                                            '& .MuiYearCalendar-root': {
                                                scrollbarWidth: 'none',
                                                '&::-webkit-scrollbar': {
                                                    display: 'none',
                                                },
                                            },
                                            '& .MuiDayCalendar-weekDayLabel': {
                                                color: '#000',
                                                fontWeight: 500,
                                            },
                                            '& .MuiPickersDay-root': {
                                                color: '#000',
                                                '&:hover': {
                                                    backgroundColor: '#f0f0f0',
                                                },
                                                '&.Mui-selected': {
                                                    backgroundColor: '#e0e0e0',
                                                    color: '#000',
                                                    '&:hover': {
                                                        backgroundColor: '#d5d5d5',
                                                    },
                                                },
                                                '&.Mui-disabled': {
                                                    color: '#ccc',
                                                },
                                            },
                                            '& .MuiPickersCalendarHeader-label': {
                                                color: '#000',
                                                fontWeight: 500,
                                            },
                                            '& .MuiPickersArrowSwitcher-button': {
                                                color: '#000',
                                            },
                                            '& svg': {
                                                color: '#000',
                                            },
                                        },
                                    },
                                }}
                            />
                        </LocalizationProvider>

                        {isUnderage && (
                            <Grid container alignItems="center" direction="row">
                                <Grid item>
                                    <InfoOutlinedIcon sx={{ color: '#333333', mr: 1, fontSize: "16px" }} />
                                </Grid>
                                <Grid item xs>
                                    <Typography
                                        sx={{
                                            color: '#817E7E',
                                            fontSize: '13px',
                                            mt: 1
                                        }}
                                    >
                                        Nominee age is lower than 18 years and hence requires guardian details as well (section at the bottom). Kindly fill guardian's mobile and email address instead of nominee.
                                    </Typography>
                                </Grid>
                            </Grid>
                        )}


                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            type="tel"
                            label={isUnderage ? "Mobile Number (of Guardian)" : "Mobile Number"}
                            variant="outlined"
                            size="small"
                            value={isUnderage ? guardian.guardianMobile : nominee.nomineeMobile}
                            onChange={(e) => {
                                const onlyDigits = e.target.value.replace(/\D/g, '')
                                if (onlyDigits.length <= 10) {
                                    if (isUnderage) {
                                        handleGuardianChange('guardianMobile', onlyDigits)
                                    } else {
                                        handleNomineeChange('nomineeMobile', onlyDigits)
                                    }
                                }
                            }}
                            error={isUnderage ? !!errors.guardianMobile : !!errors.nomineeMobile}
                            helperText={isUnderage ? errors.guardianMobile : errors.nomineeMobile}
                            InputProps={{
                                sx: {
                                    color: '#000',
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#999',
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#000',
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#999',
                                    },
                                }
                            }}
                            InputLabelProps={{
                                sx: {
                                    color: '#000',

                                    '&.Mui-focused': {
                                        color: '#333333',
                                    }
                                }
                            }}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label={isUnderage ? "Enter Email (of Guardian)" : "Enter Email"}
                            variant="outlined"
                            size="small"
                            value={isUnderage ? guardian.guardianEmail : nominee.nomineeEmail}
                            onChange={(e) => {
                                const email = e.target.value;
                                if (isUnderage) {
                                    handleGuardianChange('guardianEmail', email);
                                } else {
                                    handleNomineeChange('nomineeEmail', email);
                                }
                            }}
                            error={isUnderage ? !!errors.guardianEmail : !!errors.nomineeEmail}
                            helperText={isUnderage ? errors.guardianEmail : errors.nomineeEmail}
                            InputProps={{
                                sx: {
                                    color: '#000',
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#999',
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#000',
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#999',
                                    },
                                }
                            }}
                            InputLabelProps={{
                                sx: {
                                    color: '#000',

                                    '&.Mui-focused': {
                                        color: '#333333',
                                    }
                                }
                            }}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            select
                            label="Select Proof Type"
                            variant="outlined"
                            size="small"
                            value={nominee.nomineeProofType}
                            onChange={(e) => handleNomineeChange('nomineeProofType', e.target.value)}
                            error={!!errors.nomineeProofType}
                            helperText={errors.nomineeProofType}
                            InputProps={{
                                sx: {
                                    color: '#000',
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#999',
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#000',
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#999',
                                    },
                                    '& .MuiSelect-icon': {
                                        color: '#000',
                                    },
                                }
                            }}
                            InputLabelProps={{
                                sx: {
                                    color: '#000',
                                    '&.Mui-focused': {
                                        color: '#333333',
                                    }
                                }
                            }}

                            SelectProps={{
                                MenuProps: {
                                    PaperProps: {
                                        sx: {
                                            backgroundColor: '#fff',
                                            boxShadow: 'none',
                                            border: '1px solid #ddd',
                                            borderRadius: 1,
                                        }
                                    }
                                }
                            }}
                        >
                            {proofTypeOptions.map((opt, idx) => (
                                <MenuItem key={idx} value={opt}
                                    sx={{
                                        background: '#fff',
                                        color: "#000",
                                        '&:hover': {
                                            backgroundColor: '#f0f0f0',
                                        },
                                        '&.Mui-selected': {
                                            backgroundColor: '#e0e0e0',
                                        }
                                    }}
                                >
                                    {opt}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Enter Identity Proof Number"
                            variant="outlined"
                            size="small"
                            value={nominee.nomineeProofNumber}
                            onChange={(e) => handleNomineeChange('nomineeProofNumber', e.target.value)}
                            inputProps={{ maxLength: 20 }}
                            error={!!errors.nomineeProofNumber}
                            helperText={errors.nomineeProofNumber}
                            InputProps={{
                                sx: {
                                    color: '#000',
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#999',
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#000',
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#999',
                                    },
                                }
                            }}
                            InputLabelProps={{
                                sx: {
                                    color: '#000',

                                    '&.Mui-focused': {
                                        color: '#333333',
                                    }
                                }
                            }}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Typography sx={{ color: "#333333", fontSize: "14px" }}>
                            Please Provide Nominee Address
                        </Typography>
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Address"
                            variant="outlined"
                            multiline
                            rows={2}
                            size="small"
                            value={nominee.nomineeAddress}
                            onChange={(e) => handleNomineeChange('nomineeAddress', e.target.value)}
                            error={!!errors.nomineeAddress}
                            helperText={errors.nomineeAddress}
                            InputProps={{
                                sx: {
                                    color: '#000',
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#999',
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#000',
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#999',
                                    },
                                }
                            }}
                            InputLabelProps={{
                                sx: {
                                    color: '#000',

                                    '&.Mui-focused': {
                                        color: '#333333',
                                    }
                                }
                            }}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Enter Pincode"
                            variant="outlined"
                            size="small"
                            value={nominee.nomineePincode}
                            onChange={(e) => handleNomineeChange('nomineePincode', e.target.value)}
                            error={!!errors.nomineePincode}
                            helperText={errors.nomineePincode}
                            InputProps={{
                                sx: {
                                    color: '#000',
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#999',
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#000',
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#999',
                                    },
                                }
                            }}
                            InputLabelProps={{
                                sx: {
                                    color: '#000',

                                    '&.Mui-focused': {
                                        color: '#333333',
                                    }
                                }
                            }}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Enter City"
                            variant="outlined"
                            size="small"
                            value={nominee.nomineeCity}
                            onChange={(e) => handleNomineeChange('nomineeCity', e.target.value)}
                            error={!!errors.nomineeCity}
                            helperText={errors.nomineeCity}
                            InputProps={{
                                sx: {
                                    color: '#000',
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#999',
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#000',
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#999',
                                    },
                                }
                            }}
                            InputLabelProps={{
                                sx: {
                                    color: '#000',

                                    '&.Mui-focused': {
                                        color: '#333333',
                                    }
                                }
                            }}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            select
                            label="Select State"
                            variant="outlined"
                            size="small"
                            value={nominee.nomineeState}
                            onChange={(e) => handleNomineeChange('nomineeState', e.target.value)}
                            error={!!errors.nomineeState}
                            helperText={errors.nomineeState}
                            InputProps={{
                                sx: {
                                    color: '#000',
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#999',
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#000',
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#999',
                                    },
                                    '& .MuiSelect-icon': {
                                        color: '#000',
                                    },
                                }
                            }}
                            InputLabelProps={{
                                sx: {
                                    color: '#000',
                                    '&.Mui-focused': {
                                        color: '#333333',
                                    }
                                }
                            }}

                            SelectProps={{
                                MenuProps: {
                                    PaperProps: {
                                        sx: {
                                            backgroundColor: '#fff',
                                            boxShadow: 'none',
                                            border: '1px solid #ddd',
                                            borderRadius: 1,
                                        }
                                    }
                                }
                            }}
                        >
                            {stateOptions.map((opt, idx) => (
                                <MenuItem key={idx} value={opt}
                                    sx={{
                                        background: '#fff',
                                        color: "#000",
                                        '&:hover': {
                                            backgroundColor: '#f0f0f0',
                                        },
                                        '&.Mui-selected': {
                                            backgroundColor: '#e0e0e0',
                                        }
                                    }}
                                >
                                    {opt}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Enter Country"
                            variant="outlined"
                            size="small"
                            value={nominee.nomineeCountry}
                            onChange={(e) => handleNomineeChange('nomineeCountry', e.target.value)}
                            error={!!errors.nomineeCountry}
                            helperText={errors.nomineeCountry}
                            InputProps={{
                                sx: {
                                    color: '#000',
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#999',
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#000',
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#999',
                                    },
                                }
                            }}
                            InputLabelProps={{
                                sx: {
                                    color: '#000',

                                    '&.Mui-focused': {
                                        color: '#333333',
                                    }
                                }
                            }}
                        />
                    </Grid>

                    {isUnderage && <>
                        <Grid item xs={12}>
                            <Typography sx={{ color: "#333333", fontSize: "14px" }}>
                                Guardian Details
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Enter Full Name"
                                variant="outlined"
                                size="small"
                                value={guardian.guardianName}
                                onChange={(e) => handleGuardianChange('guardianName', e.target.value)}
                                error={!!errors.guardianName}
                                helperText={errors.guardianName}
                                InputProps={{
                                    sx: {
                                        color: '#000',
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#999',
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#000',
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#999',
                                        },
                                    }
                                }}
                                InputLabelProps={{
                                    sx: {
                                        color: '#000',

                                        '&.Mui-focused': {
                                            color: '#333333',
                                        }
                                    }
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                select
                                label="Relationship with Nominee"
                                variant="outlined"
                                size="small"
                                value={guardian.guardianRelationship}
                                onChange={(e) => handleGuardianChange('guardianRelationship', e.target.value)}
                                error={!!errors.guardianRelationship}
                                helperText={errors.guardianRelationship}
                                InputProps={{
                                    sx: {
                                        color: '#000',
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#999',
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#000',
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#999',
                                        },
                                        '& .MuiSelect-icon': {
                                            color: '#000',
                                        },
                                    }
                                }}
                                InputLabelProps={{
                                    sx: {
                                        color: '#000',
                                        '&.Mui-focused': {
                                            color: '#333333',
                                        }
                                    }
                                }}

                                SelectProps={{
                                    MenuProps: {
                                        PaperProps: {
                                            sx: {
                                                backgroundColor: '#fff',
                                                boxShadow: 'none',
                                                border: '1px solid #ddd',
                                                borderRadius: 1,
                                            }
                                        }
                                    }
                                }}
                            >
                                {relationshipOptions.map((opt, idx) => (
                                    <MenuItem key={idx} value={opt}
                                        sx={{
                                            background: '#fff',
                                            color: "#000",
                                            '&:hover': {
                                                backgroundColor: '#f0f0f0',
                                            },
                                            '&.Mui-selected': {
                                                backgroundColor: '#e0e0e0',
                                            }
                                        }}
                                    >
                                        {opt}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    label="Date of Birth"
                                    format="DD/MM/YYYY"
                                    value={guardian.guardianDob}
                                    onChange={(newVal) => {
                                        handleGuardianChange('guardianDob', newVal)
                                        if (newVal) {
                                            const age = checkAge(newVal)
                                            setIsGuardianage(age < 18)
                                        }
                                    }}
                                    maxDate={dayjs()}
                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                            size: "small",
                                            variant: "outlined",
                                            error: !!errors.guardianDob,
                                            helperText: errors.guardianDob,
                                            InputLabelProps: {
                                                sx: {
                                                    color: '#000',
                                                    '&.Mui-focused': {
                                                        color: '#333333',
                                                    },
                                                },
                                            },
                                            InputProps: {
                                                sx: {
                                                    color: '#000',
                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: '#999',
                                                    },
                                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: '#000',
                                                    },
                                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: '#999',
                                                    },
                                                    '& .MuiSvgIcon-root': {
                                                        color: '#000', // calendar icon color
                                                    },
                                                },
                                            },

                                        },
                                        desktopPaper: {
                                            sx: {
                                                backgroundColor: '#fff',
                                                color: '#000',
                                                border: '1px solid #ddd',
                                                boxShadow: 'none',
                                                borderRadius: 1,
                                                '& .MuiYearCalendar-root': {
                                                    scrollbarWidth: 'none',
                                                    '&::-webkit-scrollbar': {
                                                        display: 'none',
                                                    },
                                                },
                                                '& .MuiDayCalendar-weekDayLabel': {
                                                    color: '#000',
                                                    fontWeight: 500,
                                                },
                                                '& .MuiPickersDay-root': {
                                                    color: '#000',
                                                    '&:hover': {
                                                        backgroundColor: '#f0f0f0',
                                                    },
                                                    '&.Mui-selected': {
                                                        backgroundColor: '#e0e0e0',
                                                        color: '#000',
                                                        '&:hover': {
                                                            backgroundColor: '#d5d5d5',
                                                        },
                                                    },
                                                    '&.Mui-disabled': {
                                                        color: '#ccc',
                                                    },
                                                },
                                                '& .MuiPickersCalendarHeader-label': {
                                                    color: '#000',
                                                    fontWeight: 500,
                                                },
                                                '& .MuiPickersArrowSwitcher-button': {
                                                    color: '#000',
                                                },
                                                '& svg': {
                                                    color: '#000',
                                                },
                                            },
                                        },
                                    }}
                                />
                            </LocalizationProvider>

                            {isGuardianage && (
                                <Grid container alignItems="center" direction="row">
                                    <Grid item>
                                        <InfoOutlinedIcon sx={{ color: 'red', mr: 1, fontSize: "16px", mt: 1 }} />
                                    </Grid>
                                    <Grid item xs>
                                        <Typography
                                            sx={{
                                                color: 'red',
                                                fontSize: '13px',
                                                mt: 1
                                            }}
                                        >
                                            Guardian cannot be below 18 years of age.
                                        </Typography>
                                    </Grid>
                                </Grid>
                            )}


                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                select
                                label="Select Proof Type"
                                variant="outlined"
                                size="small"
                                value={guardian.guardianProofType}
                                onChange={(e) => handleGuardianChange('guardianProofType', e.target.value)}
                                error={!!errors.guardianProofType}
                                helperText={errors.guardianProofType}
                                InputProps={{
                                    sx: {
                                        color: '#000',
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#999',
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#000',
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#999',
                                        },
                                        '& .MuiSelect-icon': {
                                            color: '#000',
                                        },
                                    }
                                }}
                                InputLabelProps={{
                                    sx: {
                                        color: '#000',
                                        '&.Mui-focused': {
                                            color: '#333333',
                                        }
                                    }
                                }}

                                SelectProps={{
                                    MenuProps: {
                                        PaperProps: {
                                            sx: {
                                                backgroundColor: '#fff',
                                                boxShadow: 'none',
                                                border: '1px solid #ddd',
                                                borderRadius: 1,
                                            }
                                        }
                                    }
                                }}
                            >
                                {proofTypeOptions.map((opt, idx) => (
                                    <MenuItem key={idx} value={opt}
                                        sx={{
                                            background: '#fff',
                                            color: "#000",
                                            '&:hover': {
                                                backgroundColor: '#f0f0f0',
                                            },
                                            '&.Mui-selected': {
                                                backgroundColor: '#e0e0e0',
                                            }
                                        }}
                                    >
                                        {opt}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Enter Identity Proof Number"
                                variant="outlined"
                                size="small"
                                value={guardian.guardianProofNumber}
                                onChange={(e) => handleGuardianChange('guardianProofNumber', e.target.value)}
                                inputProps={{ maxLength: 20 }}
                                error={!!errors.guardianProofNumber}
                                helperText={errors.guardianProofNumber}
                                InputProps={{
                                    sx: {
                                        color: '#000',
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#999',
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#000',
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#999',
                                        },
                                    }
                                }}
                                InputLabelProps={{
                                    sx: {
                                        color: '#000',

                                        '&.Mui-focused': {
                                            color: '#333333',
                                        }
                                    }
                                }}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={isSameAsNominee}
                                        onChange={(e) => handleGuardianAddressCheckbox(e.target.checked)}
                                        color="primary"
                                        sx={{ color: '#000' }}
                                    />
                                }
                                label="Guardian Address same as Nominee Address"
                                sx={{ color: '#000' }}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Typography sx={{ color: "#333333", fontSize: "14px" }}>
                                Please Provide Guardian Address
                            </Typography>
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Address"
                                variant="outlined"
                                multiline
                                rows={2}
                                size="small"
                                value={guardian.guardianAddress}
                                onChange={(e) => handleGuardianChange('guardianAddress', e.target.value)}
                                error={!!errors.guardianAddress}
                                helperText={errors.guardianAddress}
                                InputProps={{
                                    sx: {
                                        color: '#000',
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#999',
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#000',
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#999',
                                        },
                                    }
                                }}
                                InputLabelProps={{
                                    sx: {
                                        color: '#000',

                                        '&.Mui-focused': {
                                            color: '#333333',
                                        }
                                    }
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Enter Pincode"
                                variant="outlined"
                                size="small"
                                value={guardian.guardianPincode}
                                onChange={(e) => handleGuardianChange('guardianPincode', e.target.value)}
                                error={!!errors.guardianPincode}
                                helperText={errors.guardianPincode}
                                InputProps={{
                                    sx: {
                                        color: '#000',
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#999',
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#000',
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#999',
                                        },
                                    }
                                }}
                                InputLabelProps={{
                                    sx: {
                                        color: '#000',

                                        '&.Mui-focused': {
                                            color: '#333333',
                                        }
                                    }
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Enter City"
                                variant="outlined"
                                size="small"
                                value={guardian.guardianCity}
                                onChange={(e) => handleGuardianChange('guardianCity', e.target.value)}
                                error={!!errors.guardianCity}
                                helperText={errors.guardianCity}
                                InputProps={{
                                    sx: {
                                        color: '#000',
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#999',
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#000',
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#999',
                                        },
                                    }
                                }}
                                InputLabelProps={{
                                    sx: {
                                        color: '#000',

                                        '&.Mui-focused': {
                                            color: '#333333',
                                        }
                                    }
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                select
                                label="Select State"
                                variant="outlined"
                                size="small"
                                value={guardian.guardianState}
                                onChange={(e) => handleGuardianChange('guardianState', e.target.value)}
                                error={!!errors.guardianState}
                                helperText={errors.guardianState}
                                InputProps={{
                                    sx: {
                                        color: '#000',
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#999',
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#000',
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#999',
                                        },
                                        '& .MuiSelect-icon': {
                                            color: '#000',
                                        },
                                    }
                                }}
                                InputLabelProps={{
                                    sx: {
                                        color: '#000',
                                        '&.Mui-focused': {
                                            color: '#333333',
                                        }
                                    }
                                }}

                                SelectProps={{
                                    MenuProps: {
                                        PaperProps: {
                                            sx: {
                                                backgroundColor: '#fff',
                                                boxShadow: 'none',
                                                border: '1px solid #ddd',
                                                borderRadius: 1,
                                            }
                                        }
                                    }
                                }}
                            >
                                {stateOptions.map((opt, idx) => (
                                    <MenuItem key={idx} value={opt}
                                        sx={{
                                            background: '#fff',
                                            color: "#000",
                                            '&:hover': {
                                                backgroundColor: '#f0f0f0',
                                            },
                                            '&.Mui-selected': {
                                                backgroundColor: '#e0e0e0',
                                            }
                                        }}
                                    >
                                        {opt}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Enter Country"
                                variant="outlined"
                                size="small"
                                value={guardian.guardianCountry}
                                onChange={(e) => handleGuardianChange('guardianCountry', e.target.value)}
                                error={!!errors.guardianCountry}
                                helperText={errors.guardianCountry}
                                InputProps={{
                                    sx: {
                                        color: '#000',
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#999',
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#000',
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#999',
                                        },
                                    }
                                }}
                                InputLabelProps={{
                                    sx: {
                                        color: '#000',

                                        '&.Mui-focused': {
                                            color: '#333333',
                                        }
                                    }
                                }}
                            />
                        </Grid>
                    </>}

                    <Grid item xs={12} display="flex" justifyContent="flex-end">
                        <Button onClick={() => handleSaveNext(index)}>
                            Save & Next
                        </Button>
                    </Grid>
                </Grid>
            </Collapse>
        </Paper>
    );
};

export default NomineeForm;
